import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import ReactPlayer from 'react-player'
import PlayArrowIcon from 'components/material-symbol/icons/PlayArrow'
import MoreVertIcon from 'components/material-symbol/icons/MoreVert'
import FileDownloadIcon from 'components/material-symbol/icons/FileDownload'
import MaterialSymbol from 'components/material-symbol/material-symbol'
import { sanitizeAttachmentUrl } from './messaging-utils'

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 2]

const PauseIcon = props => (
  <MaterialSymbol name="pause" fontSize={24} htmlColor="#FFFFFF" {...props} />
)

const FullscreenIcon = props => (
  <MaterialSymbol
    name="fullscreen"
    fontSize={20}
    htmlColor="#172024"
    {...props}
  />
)

const FullscreenExitIcon = props => (
  <MaterialSymbol
    name="fullscreen_exit"
    fontSize={20}
    htmlColor="#172024"
    {...props}
  />
)

const VolumeUpIcon = props => (
  <MaterialSymbol
    name="volume_up"
    fontSize={20}
    htmlColor="#172024"
    {...props}
  />
)

const VolumeOffIcon = props => (
  <MaterialSymbol
    name="volume_off"
    fontSize={20}
    htmlColor="#172024"
    {...props}
  />
)

const SpeedIcon = props => (
  <MaterialSymbol name="speed" fontSize={20} htmlColor="#172024" {...props} />
)

const PictureInPictureIcon = props => (
  <MaterialSymbol
    name="picture_in_picture_alt"
    fontSize={20}
    htmlColor="#172024"
    {...props}
  />
)

const VideoMenuItem = ({
  children,
  download,
  href,
  icon: Icon,
  onClick,
  rel,
  target,
}) => {
  const className = 'messaging-message-action-card'
  const content = (
    <>
      <Icon className="messaging-message-action-card__icon" />
      <span className="messaging-message-action-card__label">{children}</span>
    </>
  )

  if (href) {
    return (
      <a
        className={className}
        download={download}
        href={href}
        rel={rel}
        role="menuitem"
        target={target}
      >
        {content}
      </a>
    )
  }

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      role="menuitem"
    >
      {content}
    </button>
  )
}

const MessagingVideoPlayer = ({
  className,
  config,
  controls: _controls,
  height,
  url,
  width,
  ...rest
}) => {
  const wrapperRef = useRef(null)
  const playerRef = useRef(null)
  const menuRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [played, setPlayed] = useState(0)
  const [duration, setDuration] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)
  const [muted, setMuted] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const playerConfig = useMemo(() => {
    const fileConfig = config?.file ?? {}

    return {
      ...config,
      file: {
        ...fileConfig,
        attributes: {
          ...fileConfig.attributes,
          controls: false,
          playsInline: true,
        },
      },
    }
  }, [config])

  const getVideoElement = useCallback(() => {
    const internalPlayer = playerRef.current?.getInternalPlayer()
    return internalPlayer instanceof HTMLVideoElement ? internalPlayer : null
  }, [])

  useEffect(() => {
    if (!menuOpen) return undefined

    const handlePointerDown = event => {
      if (menuRef.current?.contains(event.target)) return
      setMenuOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [menuOpen])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === wrapperRef.current)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  const handleSeek = useCallback(
    event => {
      if (!duration) return

      const rect = event.currentTarget.getBoundingClientRect()
      const fraction = (event.clientX - rect.left) / rect.width
      playerRef.current?.seekTo(fraction, 'fraction')
    },
    [duration],
  )

  const handleToggleFullscreen = useCallback(async () => {
    if (!wrapperRef.current) return

    try {
      if (document.fullscreenElement === wrapperRef.current) {
        await document.exitFullscreen()
      } else {
        await wrapperRef.current.requestFullscreen()
      }
    } catch (error) {
      // Fullscreen may be blocked by the browser.
    }
  }, [])

  const handleToggleMute = useCallback(() => {
    const video = getVideoElement()
    if (!video) return

    video.muted = !video.muted
    setMuted(video.muted)
  }, [getVideoElement])

  const handleCyclePlaybackRate = useCallback(() => {
    const video = getVideoElement()
    if (!video) return

    const currentIndex = PLAYBACK_RATES.indexOf(playbackRate)
    const nextRate =
      PLAYBACK_RATES[(currentIndex + 1) % PLAYBACK_RATES.length] ?? 1

    video.playbackRate = nextRate
    setPlaybackRate(nextRate)
  }, [getVideoElement, playbackRate])

  const handlePictureInPicture = useCallback(async () => {
    const video = getVideoElement()
    if (!video || !document.pictureInPictureEnabled) return

    try {
      if (document.pictureInPictureElement === video) {
        await document.exitPictureInPicture()
      } else {
        await video.requestPictureInPicture()
      }
      setMenuOpen(false)
    } catch (error) {
      // PiP may be blocked by the browser.
    }
  }, [getVideoElement])

  const safeUrl = sanitizeAttachmentUrl(url)
  if (!safeUrl) return null

  return (
    <div ref={wrapperRef} className={clsx('messaging-video-player', className)}>
      <ReactPlayer
        ref={playerRef}
        config={playerConfig}
        controls={false}
        height={height}
        muted={muted}
        playing={playing}
        playbackRate={playbackRate}
        url={safeUrl}
        width={width}
        onDuration={setDuration}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
        onProgress={({ played: nextPlayed }) => setPlayed(nextPlayed)}
        {...rest}
      />
      <div className="messaging-video-player__controls">
        <button
          type="button"
          className="messaging-video-player__play-button"
          aria-label={playing ? 'Pause video' : 'Play video'}
          onClick={() => setPlaying(current => !current)}
        >
          {playing ? (
            <PauseIcon />
          ) : (
            <PlayArrowIcon fontSize={24} htmlColor="#FFFFFF" />
          )}
        </button>
        <button
          type="button"
          className="messaging-video-player__progress"
          aria-label="Seek video"
          onClick={handleSeek}
        >
          <span
            className="messaging-video-player__progress-fill"
            style={{ width: `${played * 100}%` }}
          />
        </button>
        <div ref={menuRef} className="messaging-video-player__menu">
          <button
            type="button"
            className="messaging-video-player__menu-button"
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-label="Video options"
            onClick={() => setMenuOpen(current => !current)}
          >
            <MoreVertIcon fontSize={24} htmlColor="#FFFFFF" />
          </button>
          {menuOpen && (
            <div
              className="messaging-message-actions-list messaging-video-player__menu-panel"
              role="menu"
            >
              <VideoMenuItem
                icon={isFullscreen ? FullscreenExitIcon : FullscreenIcon}
                onClick={handleToggleFullscreen}
              >
                {isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              </VideoMenuItem>
              <VideoMenuItem
                download
                href={safeUrl}
                icon={FileDownloadIcon}
                rel="noopener noreferrer"
                target="_blank"
              >
                Download
              </VideoMenuItem>
              <VideoMenuItem
                icon={muted ? VolumeOffIcon : VolumeUpIcon}
                onClick={handleToggleMute}
              >
                {muted ? 'Unmute' : 'Mute'}
              </VideoMenuItem>
              <VideoMenuItem icon={SpeedIcon} onClick={handleCyclePlaybackRate}>
                Playback speed ({playbackRate}x)
              </VideoMenuItem>
              <VideoMenuItem
                icon={PictureInPictureIcon}
                onClick={handlePictureInPicture}
              >
                Picture in Picture
              </VideoMenuItem>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MessagingVideoPlayer
